import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { ArrowLeft, Users, Building2, TrendingUp, Eye, EyeOff, Edit, Trash2, Plus, Activity, MapPin, Clock, UserPlus } from 'lucide-react';

const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [revealedPasswords, setRevealedPasswords] = useState({});
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    is_admin: false
  });

  useEffect(() => {
    fetchAnalytics();
    fetchUsers();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/analytics/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to fetch analytics');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/analytics/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
      console.error(error);
    }
  };

  const populateDemoData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/analytics/populate-demo-data`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Created ${response.data.users_created} users and ${response.data.login_records_created} login records`);
      fetchAnalytics();
      fetchUsers();
    } catch (error) {
      toast.error('Failed to populate demo data');
    }
  };

  const togglePasswordVisibility = (userId) => {
    setRevealedPasswords(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      password: '',
      is_admin: user.is_admin
    });
    setEditDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingUser(null);
    setFormData({
      username: '',
      email: '',
      password: '',
      is_admin: false
    });
    setCreateDialogOpen(true);
  };

  const handleUpdateUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const updateData = {
        username: formData.username,
        email: formData.email,
        is_admin: formData.is_admin
      };
      if (formData.password) {
        updateData.password = formData.password;
      }

      await axios.put(`${API}/analytics/users/${editingUser.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User updated successfully');
      setEditDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleCreateUser = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/analytics/users/create`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User created successfully');
      setCreateDialogOpen(false);
      fetchUsers();
      fetchAnalytics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleDeleteUser = async (userId, username) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/analytics/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`User ${username} deleted successfully`);
      fetchUsers();
      fetchAnalytics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0E27]">
        <div className="inline-block h-12 w-12 animate-spin rounded-full border-2 border-solid border-[#D4AF37] border-r-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0E27]">
      {/* Header */}
      <header className="crystal-glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-2xl font-semibold text-[#D4AF37]">📊 Analytics Dashboard</h1>
              <p className="text-sm text-[#94A3B8]">System Overview & User Management</p>
            </div>
            <div className="flex gap-3">
              <Button 
                onClick={populateDemoData}
                variant="outline"
                className="border-[#D4AF37]/20 text-[#D4AF37] hover:bg-[#D4AF37]/10"
              >
                <Plus className="w-4 h-4 mr-2" />
                Populate Demo Data
              </Button>
              <Button 
                onClick={() => navigate('/admin/dashboard')} 
                variant="outline"
                className="border-white/10 text-[#94A3B8] hover:bg-white/5 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-8 h-8 opacity-80" />
              <span className="text-3xl font-bold">{analytics?.total_users || 0}</span>
            </div>
            <p className="text-sm opacity-90">Total Users</p>
            <p className="text-xs opacity-75 mt-1">{analytics?.total_admins || 0} Admins, {analytics?.total_clients || 0} Clients</p>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <Building2 className="w-8 h-8 opacity-80" />
              <span className="text-3xl font-bold">{analytics?.total_properties || 0}</span>
            </div>
            <p className="text-sm opacity-90">Total Properties</p>
            <p className="text-xs opacity-75 mt-1">
              {analytics?.properties_by_listing?.Sale || 0} Sale, {analytics?.properties_by_listing?.Rental || 0} Rent
            </p>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-8 h-8 opacity-80" />
              <span className="text-3xl font-bold">{analytics?.properties_with_banners || 0}</span>
            </div>
            <p className="text-sm opacity-90">Featured Properties</p>
            <p className="text-xs opacity-75 mt-1">With promotional banners</p>
          </div>

          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <Activity className="w-8 h-8 opacity-80" />
              <span className="text-3xl font-bold">{analytics?.active_users || 0}</span>
            </div>
            <p className="text-sm opacity-90">Active Users</p>
            <p className="text-xs opacity-75 mt-1">Logged in (last 30 min)</p>
          </div>
        </div>

        {/* Property Type Breakdown */}
        <div className="bg-[#131A38] rounded-2xl p-6 mb-8 border border-white/5">
          <h2 className="font-heading text-2xl font-semibold text-[#D4AF37] mb-4">📈 Property Statistics</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {Object.entries(analytics?.properties_by_type || {}).map(([type, count]) => (
              <div key={type} className="text-center p-4 bg-[#0A0E27] rounded-xl border border-white/5">
                <div className="text-3xl font-bold text-[#D4AF37]">{count}</div>
                <div className="text-sm text-[#94A3B8] mt-1">{type}</div>
              </div>
            ))}
          </div>
        </div>

        {/* User Management */}
        <div className="bg-[#131A38] rounded-2xl p-6 mb-8 border border-white/5">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-2xl font-semibold text-[#D4AF37]">👥 User Management</h2>
            <Button
              onClick={openCreateDialog}
              className="bg-[#D4AF37] hover:bg-[#E2C364] text-[#0A0E27] font-semibold rounded-full"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Create User
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-3 text-[#94A3B8] text-xs uppercase tracking-wider font-medium text-white">Username</th>
                  <th className="text-left p-3 text-[#94A3B8] text-xs uppercase tracking-wider font-medium text-white">Email</th>
                  <th className="text-left p-3 text-[#94A3B8] text-xs uppercase tracking-wider font-medium text-white">Password</th>
                  <th className="text-left p-3 text-[#94A3B8] text-xs uppercase tracking-wider font-medium text-white">Type</th>
                  <th className="text-left p-3 text-[#94A3B8] text-xs uppercase tracking-wider font-medium text-white">Created</th>
                  <th className="text-center p-3 text-amber-900 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-3 font-medium text-white">{user.username}</td>
                    <td className="p-3 text-[#94A3B8]">{user.email}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm">
                          {revealedPasswords[user.id] ? user.password_hash : '••••••••'}
                        </span>
                        <button
                          onClick={() => togglePasswordVisibility(user.id)}
                          className="text-[#D4AF37] hover:text-[#E2C364]"
                        >
                          {revealedPasswords[user.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        user.is_admin ? 'bg-purple-500/20 text-purple-300' : 'bg-blue-500/20 text-blue-300'
                      }`}>
                        {user.is_admin ? 'Admin' : 'Client'}
                      </span>
                    </td>
                    <td className="p-3 text-sm text-[#94A3B8]">{formatDate(user.created_at)}</td>
                    <td className="p-3">
                      <div className="flex gap-2 justify-center">
                        <Button
                          onClick={() => openEditDialog(user)}
                          variant="outline"
                          size="sm"
                          className="border-white/10 text-[#94A3B8] hover:bg-white/5"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" className="border-red-500/20 text-red-400 hover:bg-red-500/10">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-[#131A38] border-white/10 text-white">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete User</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete user "{user.username}"? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDeleteUser(user.id, user.username)} className="bg-red-600 hover:bg-red-700">
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Login Activity */}
        <div className="bg-[#131A38] rounded-2xl p-6 border border-white/5">
          <h2 className="font-heading text-2xl font-semibold text-[#D4AF37] mb-4">🔐 Recent Login Activity</h2>
          <div className="space-y-3">
            {analytics?.recent_logins?.slice(0, 10).map((login, idx) => (
              <div key={idx} className="flex items-center justify-between p-4 bg-[#0A0E27] rounded-xl border border-white/5">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-full flex items-center justify-center">
                    <Users className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <div className="font-semibold text-white">{login.username}</div>
                    <div className="flex items-center gap-3 text-sm text-[#94A3B8] mt-1">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(login.login_time)}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {login.location || 'Unknown'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-xs text-[#475569] font-mono">{login.ip_address}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-[#131A38] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-[#D4AF37]">Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Username</Label>
              <Input
                value={formData.username}
                onChange={(e) => setFormData({...formData, username: e.target.value})}
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
              />
            </div>
            <div>
              <Label>New Password (leave empty to keep current)</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_admin}
                onChange={(e) => setFormData({...formData, is_admin: e.target.checked})}
                className="h-4 w-4"
              />
              <Label>Admin User</Label>
            </div>
            <Button onClick={handleUpdateUser} className="w-full bg-[#D4AF37] hover:bg-[#E2C364] text-[#0A0E27] font-semibold rounded-full">
              Update User
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-[#131A38] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-[#D4AF37]">Create New User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Username *</Label>
              <Input
                value={formData.username}
                onChange={(e) => setFormData({...formData, username: e.target.value})}
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
              />
            </div>
            <div>
              <Label>Password *</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_admin}
                onChange={(e) => setFormData({...formData, is_admin: e.target.checked})}
                className="h-4 w-4"
              />
              <Label>Admin User</Label>
            </div>
            <Button onClick={handleCreateUser} className="w-full bg-[#D4AF37] hover:bg-[#E2C364] text-[#0A0E27] font-semibold rounded-full">
              Create User
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AnalyticsDashboard;
